import { create } from 'zustand';

/**
 * The authenticator app step of a login, between the password and the token.
 * Memory only, never storage: the setup token opens the setup endpoints, and
 * a refresh simply sends the user back to the login form.
 */
type TotpChallenge = { challengeId: string; expiresAt: number };
type TotpSetup = { setupToken: string; expiresAt: number };

type TotpLoginState = {
  challenge?: TotpChallenge;
  setup?: TotpSetup;
  startChallenge: (challengeId: string, expiresIn: number) => void;
  startSetup: (setupToken: string, expiresIn: number) => void;
  clear: () => void;
};

const expiresAt = (expiresIn: number) => Date.now() + expiresIn * 1000;

const useTotpLoginStore = create<TotpLoginState>()(set => ({
  challenge: undefined,
  setup: undefined,
  startChallenge: (challengeId, expiresIn) =>
    set({
      challenge: { challengeId, expiresAt: expiresAt(expiresIn) },
      setup: undefined,
    }),
  startSetup: (setupToken, expiresIn) =>
    set({
      setup: { setupToken, expiresAt: expiresAt(expiresIn) },
      challenge: undefined,
    }),
  clear: () => set({ challenge: undefined, setup: undefined }),
}));

/** The app code: exactly 6 digits. */
const isTotpCode = (code: string) => /^\d{6}$/.test(code);

/** `XXXXX-XXXXX`; the API accepts it without the dash, spaced or lowercase. */
const isRecoveryCode = (code: string) =>
  /^[A-Z0-9]{10}$/.test(code.replace(/[\s-]/g, '').toUpperCase());

export type { TotpChallenge, TotpSetup };
export { useTotpLoginStore, isTotpCode, isRecoveryCode };
